import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Plus } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatDate, formatVisitStatus } from '../utils/formatters';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { getButtonStyle, primaryButtonStyle } from '../utils/buttonStyles';
import {
  getSelectStyle,
  getInputStyle,
  getTextareaStyle,
  labelStyle,
  errorMessageStyle,
  helpTextStyle,
  formContainerStyle,
  formActionsStyle,
  formGroupStyle
} from '../utils/formStyles';
import {
  getCardStyle,
  getCardGridStyle,
  cardTitleStyle,
  cardValueStyle,
  cardSubtextStyle,
  getCardTitleColor
} from '../utils/cardStyles';
import {
  listContainerStyle,
  listItemStyle,
  listItemHeaderStyle,
  listItemTitleStyle,
  getStatusBadgeStyle,
  getInfoGridStyle,
  observationsContainerStyle,
  loadingIndicatorStyle,
  spinnerStyle,
  emptyListStyle,
  emptyListIconStyle,
  pageHeaderStyle,
  obraCardStyle,
  obraCardHeaderStyle,
  obraCardTitleStyle,
  obraCardStatusStyle,
  obraCardBodyStyle,
  obraCardInfoLineStyle,
  obraCardLabelStyle,
  obraCardValueStyle,
  obraCardFooterStyle
} from '../utils/listStyles';

/**
 * Componente para registro e visualização de visitas a uma propriedade
 */
const HistoricoVisitas = () => {
  const { id: propriedadeId } = useParams();
  const navigate = useNavigate();
  const {
    propriedades,
    residencias,
    visitas,
    agentes,
    carregarPropriedades,
    carregarResidencias,
    carregarVisitas,
    carregarAgentes,
    registrarVisita,
    loading
  } = useFossasSepticas();

  const [showForm, setShowForm] = useState(false);
  const [propriedade, setPropriedade] = useState(null);
  const [formData, setFormData] = useState({
    propriedade_id: parseInt(propriedadeId),
    residencia_id: '',
    agente_id: '',
    data_visita: new Date().toISOString().split('T')[0],
    status: 'Realizada',
    observacoes: ''
  });
  const [errors, setErrors] = useState({});

  // Carregar dados ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      await carregarPropriedades();
      await carregarResidencias(parseInt(propriedadeId));
      await carregarVisitas(parseInt(propriedadeId));
      await carregarAgentes();
    };

    fetchData();
  }, [carregarPropriedades, carregarResidencias, carregarVisitas, carregarAgentes, propriedadeId]);

  // Atualizar propriedade quando os dados forem carregados
  useEffect(() => {
    if (propriedades.length > 0) {
      const prop = propriedades.find(p => p.id === parseInt(propriedadeId));
      if (prop) {
        setPropriedade(prop);
      } else {
        toast.error('Propriedade não encontrada');
        navigate('/fossas-septicas/propriedades');
      }
    }
  }, [propriedades, propriedadeId, navigate]);

  // Abrir formulário de registro de visita
  const handleNovaVisita = () => {
    setFormData({
      propriedade_id: parseInt(propriedadeId),
      residencia_id: '',
      agente_id: '',
      data_visita: new Date().toISOString().split('T')[0],
      status: 'Realizada',
      observacoes: ''
    });
    setShowForm(true);
    setErrors({});
  };

  // Cancelar registro
  const handleCancelar = () => {
    setShowForm(false);
  };

  // Atualizar estado do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};

    if (!formData.agente_id) {
      newErrors.agente_id = 'Agente é obrigatório';
    }

    if (!formData.data_visita) {
      newErrors.data_visita = 'Data da visita é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const success = await registrarVisita(formData);

      if (success) {
        setShowForm(false);

        // Se a visita foi realizada, perguntar se deseja cadastrar fossa séptica
        if (formData.status === 'Realizada' && formData.residencia_id) {
          if (window.confirm('Deseja cadastrar uma fossa séptica para esta residência?')) {
            navigate(`/fossas-septicas/fossas/cadastro/${formData.residencia_id}`);
          }
        }
      }
    }
  };

  if (loading.propriedades || !propriedade) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Carregando dados da propriedade...</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div style={{ ...pageHeaderStyle, alignItems: 'flex-start' }}>
        <div>
          <Link to="/fossas-septicas/propriedades" style={{
            display: 'inline-flex',
            alignItems: 'center',
            color: primaryButtonStyle.backgroundColor,
            textDecoration: 'none',
            marginBottom: '10px',
            fontSize: '14px'
          }}>
            <ArrowLeft size={16} style={{ marginRight: '5px' }} />
            Voltar para Propriedades
          </Link>
          <h2>Histórico de Visitas: {propriedade.nome}</h2>
          <p style={{ color: '#666', marginTop: '5px' }}>
            <strong>Proprietário:</strong> {propriedade.nome_proprietario} |
            <strong> Localidade:</strong> {propriedade.localidade?.nome || 'Não definida'}
          </p>
        </div>
        <button
          onClick={handleNovaVisita}
          style={getButtonStyle('primary')}
        >
          <Plus size={16} />
          Registrar Visita
        </button>
      </div>

      {showForm && (
        <div style={formContainerStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Registrar Nova Visita</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Residência (opcional)</label>
                <select
                  name="residencia_id"
                  value={formData.residencia_id}
                  onChange={handleChange}
                  style={getSelectStyle()}
                >
                  <option value="">Selecione uma residência</option>
                  {residencias.map(res => (
                    <option key={res.id} value={res.id}>{res.identificacao}</option>
                  ))}
                </select>
                <small style={helpTextStyle}>
                  Se não selecionar, a visita será registrada apenas para a propriedade.
                </small>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Agente</label>
                <select
                  name="agente_id"
                  value={formData.agente_id}
                  onChange={handleChange}
                  style={getSelectStyle({ error: !!errors.agente_id })}
                >
                  <option value="">Selecione um agente</option>
                  {agentes.filter(a => a.ativo).map(agente => (
                    <option key={agente.id} value={agente.id}>{agente.nome}</option>
                  ))}
                </select>
                {errors.agente_id && <div style={errorMessageStyle}>{errors.agente_id}</div>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Data da Visita</label>
                <input
                  type="date"
                  name="data_visita"
                  value={formData.data_visita}
                  onChange={handleChange}
                  style={getInputStyle({ error: !!errors.data_visita })}
                />
                {errors.data_visita && <div style={errorMessageStyle}>{errors.data_visita}</div>}
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  style={getSelectStyle()}
                >
                  <option value="Realizada">Realizada</option>
                  <option value="Ausente">Ausente</option>
                  <option value="Remarcada">Remarcada</option>
                </select>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Observações (opcional)</label>
              <textarea
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                style={getTextareaStyle()}
                placeholder="Informações adicionais sobre a visita"
              />
            </div>

            <div style={formActionsStyle}>
              <button
                type="button"
                onClick={handleCancelar}
                style={getButtonStyle('secondary')}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading.visitas}
                style={getButtonStyle('primary', { disabled: loading.visitas })}
              >
                {loading.visitas ? 'Registrando...' : 'Registrar Visita'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Estatísticas rápidas */}
      <div style={getCardGridStyle(4)}>
        <div style={getCardStyle('neutral')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('neutral') }}>Total de Visitas</h3>
          <div style={cardValueStyle}>
            {visitas.length}
          </div>
          <div style={cardSubtextStyle}>Visitas registradas</div>
        </div>

        <div style={getCardStyle('success')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('success') }}>Visitas Realizadas</h3>
          <div style={cardValueStyle}>
            {visitas.filter(v => v.status === 'Realizada').length}
          </div>
          <div style={cardSubtextStyle}>Visitas concluídas com sucesso</div>
        </div>

        <div style={getCardStyle('danger')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('danger') }}>Visitas Ausentes</h3>
          <div style={cardValueStyle}>
            {visitas.filter(v => v.status === 'Ausente').length}
          </div>
          <div style={cardSubtextStyle}>Visitas onde não havia ninguém</div>
        </div>

        <div style={getCardStyle('warning')}>
          <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('warning') }}>Visitas Remarcadas</h3>
          <div style={cardValueStyle}>
            {visitas.filter(v => v.status === 'Remarcada').length}
          </div>
          <div style={cardSubtextStyle}>Visitas que precisaram ser remarcadas</div>
        </div>
      </div>

      {loading.visitas && !showForm ? (
        <div style={loadingIndicatorStyle}>
          <div style={spinnerStyle}></div>
          <p>Carregando histórico de visitas...</p>
        </div>
      ) : (
        <>
          {visitas.length === 0 ? (
            <div style={emptyListStyle}>
              <Calendar size={48} style={emptyListIconStyle} />
              <h3>Nenhuma visita registrada</h3>
              <p>Esta propriedade ainda não possui visitas registradas.</p>
              <button
                onClick={handleNovaVisita}
                style={{
                  ...getButtonStyle('primary'),
                  margin: '0 auto'
                }}
              >
                <Plus size={16} />
                Registrar Primeira Visita
              </button>
            </div>
          ) : (
            <div style={listContainerStyle}>
              {visitas.map(visita => {
                const statusInfo = formatVisitStatus(visita.status);
                return (
                  <div key={visita.id} style={obraCardStyle}>
                    {/* Cabeçalho do card */}
                    <div style={obraCardHeaderStyle}>
                      <h3 style={obraCardTitleStyle}>{formatDate(visita.data_visita)}</h3>
                      <span style={{
                        ...obraCardStatusStyle,
                        backgroundColor: statusInfo.color,
                        color: 'white'
                      }}>
                        {statusInfo.text}
                      </span>
                    </div>

                    {/* Corpo do card */}
                    <div style={obraCardBodyStyle}>
                      <div style={obraCardInfoLineStyle}>
                        <span style={obraCardLabelStyle}>Residência:</span>
                        <span style={obraCardValueStyle}>{visita.residencia?.identificacao || 'Propriedade geral'}</span>
                      </div>

                      <div style={obraCardInfoLineStyle}>
                        <span style={obraCardLabelStyle}>Agente:</span>
                        <span style={obraCardValueStyle}>{visita.agente?.nome || 'Não informado'}</span>
                      </div>

                      {visita.observacoes && (
                        <div style={obraCardInfoLineStyle}>
                          <span style={obraCardLabelStyle}>Observações:</span>
                          <span style={obraCardValueStyle}>{visita.observacoes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default HistoricoVisitas;